import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAdminCompany, updateAdminCompany, getCompanyPostings } from '../../../api/adminApi';

const EDIT_PW = process.env.REACT_APP_ADMIN_EDIT_PW;

const STATUS_MAP = {
  active:    { label: '모집중',   cls: 'ag-badge--success' },
  closed:    { label: '마감',     cls: 'ag-badge--danger' },
  draft:     { label: '임시저장', cls: 'ag-badge--warn' },
};

function EditAuthModal({ onConfirm, onCancel }) {
  const [pw, setPw]       = useState('');
  const [error, setError] = useState('');
  const handleConfirm = () => {
    if (pw === EDIT_PW) { onConfirm(); }
    else { setError('수정 비밀번호가 올바르지 않습니다.'); }
  };
  return (
    <div className="ag-modal-backdrop">
      <div className="ag-modal">
        <div className="ag-modal__title">수정 인증</div>
        <div className="ag-modal__sub">수정을 위해 수정 비밀번호를 입력하세요.</div>
        <div className="ag-field" style={{ margin: '20px 0 0' }}>
          <input
            className="ag-field__input"
            type="password"
            placeholder="수정 비밀번호"
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConfirm()}
            autoFocus
          />
        </div>
        {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="ag-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>취소</button>
          <button className="ag-btn ag-btn--primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleConfirm}>확인</button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="ag-info-row">
      <span className="ag-info-row__label">{label}</span>
      <span className="ag-info-row__value">{value ?? '—'}</span>
    </div>
  );
}

export default function AdminCompanyDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [company,      setCompany]      = useState(null);
  const [postings,     setPostings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [editMode,     setEditMode]     = useState(false);
  const [showEditAuth, setShowEditAuth] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [form,         setForm]         = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [comp, posts] = await Promise.all([
          getAdminCompany(id),
          getCompanyPostings(id).catch(() => []),
        ]);
        if (cancelled) return;
        setCompany(comp);
        setPostings(posts ?? []);
        setForm({
          companyName:  comp.companyName,
          managerName:  comp.managerName,
          email:        comp.email,
          phone:        comp.phone,
        });
      } catch {
        if (!cancelled) setError('제작사 정보를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateAdminCompany(id, form);
      setCompany(prev => ({ ...prev, ...updated }));
      setEditMode(false);
    } catch {
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout pageId="companies">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
          <i className="ti ti-loader" style={{ fontSize: 24 }} /> 불러오는 중...
        </div>
      </AdminLayout>
    );
  }

  if (error || !company) {
    return (
      <AdminLayout pageId="companies">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-danger)' }}>
          {error ?? '제작사를 찾을 수 없습니다.'}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageId="companies">
      {showEditAuth && (
        <EditAuthModal
          onConfirm={() => { setShowEditAuth(false); setEditMode(true); }}
          onCancel={() => setShowEditAuth(false)}
        />
      )}

      <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
        {/* 상단 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button className="ag-btn ag-btn--sm" onClick={() => navigate('/admin/companies')}>
            <i className="ti ti-arrow-left" /> 목록
          </button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{company.companyName}</h2>
          <span className={`ag-badge ${(company.status ?? 'active') === 'active' ? 'ag-badge--success' : 'ag-badge--danger'}`}>
            {(company.status ?? 'active') === 'active' ? '활성' : '정지'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'start' }}>
          {/* 좌측: 기본 정보 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 회사 아이콘 */}
            <div className="ag-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '28px 20px' }}>
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: '#E9E9EC', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="ti ti-building" style={{ fontSize: 32, color: '#888' }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, textAlign: 'center' }}>{company.companyName}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{company.managerName || '—'}</div>
            </div>

            {/* 기본 정보 카드 */}
            <div className="ag-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div className="ag-card-title" style={{ marginBottom: 0 }}>기본 정보</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {editMode ? (
                    <>
                      <button className="ag-btn ag-btn--sm" onClick={() => setEditMode(false)} disabled={saving}>취소</button>
                      <button className="ag-btn ag-btn--primary ag-btn--sm" onClick={handleSave} disabled={saving}>
                        <i className="ti ti-check" /> {saving ? '저장 중...' : '저장'}
                      </button>
                    </>
                  ) : (
                    <button className="ag-btn ag-btn--sm" onClick={() => setShowEditAuth(true)}>
                      <i className="ti ti-edit" /> 수정
                    </button>
                  )}
                </div>
              </div>

              {editMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { label: '회사명',   key: 'companyName' },
                    { label: '담당자',   key: 'managerName' },
                    { label: '이메일',   key: 'email' },
                    { label: '전화번호', key: 'phone' },
                  ].map(({ label, key }) => (
                    <div key={key} className="ag-field" style={{ display: 'flex', alignItems: 'center', gap: 12, margin: 0 }}>
                      <label className="ag-field__label" style={{ width: 70, flexShrink: 0, margin: 0 }}>{label}</label>
                      <input
                        className="ag-field__input"
                        value={form[key] ?? ''}
                        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <InfoRow label="회사명"   value={company.companyName} />
                  <InfoRow label="담당자"   value={company.managerName} />
                  <InfoRow label="이메일"   value={company.email} />
                  <InfoRow label="전화번호" value={company.phone} />
                  <InfoRow label="가입일"   value={company.joinedAt?.slice(0, 10)} />
                  <InfoRow label="활성 공고" value={company.activePostings != null ? `${company.activePostings}건` : null} />
                </>
              )}
            </div>
          </div>

          {/* 우측: 공고 목록 */}
          <div className="ag-card">
            <div className="ag-card-title">작성한 공고</div>
            {postings.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 14 }}>
                작성한 공고가 없습니다.
              </div>
            ) : (
              <table className="ag-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>공고 제목</th>
                    <th>상태</th>
                    <th>지원자</th>
                    <th>마감일</th>
                    <th>등록일</th>
                  </tr>
                </thead>
                <tbody>
                  {postings.map(p => {
                    const s = STATUS_MAP[p.status] ?? { label: p.status ?? '—', cls: '' };
                    return (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 500 }}>{p.title}</td>
                        <td><span className={`ag-badge ${s.cls}`}>{s.label}</span></td>
                        <td style={{ color: 'var(--color-text-secondary)' }}>{p.applicationCount ?? 0}명</td>
                        <td style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>{p.deadline?.slice(0, 10) ?? '—'}</td>
                        <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>{p.createdAt?.slice(0, 10) ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
